import { inject } from "@angular/core";
import { patchState, signalStoreFeature, type, withMethods } from "@ngrx/signals";
import { rxMethod } from "@ngrx/signals/rxjs-interop";
import { tapResponse } from "@ngrx/operators";
import { IAuthenticationState } from "./auth.state";
import { AuthLogin } from "../../models/auth/auth-login.model";
import { AuthRegister } from "../../models/auth/auth-register.model";
import { exhaustMap, pipe, tap } from "rxjs";
import { HttpErrorResponse } from "@angular/common/http";
import { AuthService } from "../../services/auth/auth.service";
import { UserProfileService } from "../../services/user-profile/user-profile.service";
import { AuthToken } from "../../models/auth/auth-token.model";
import { withAuthenticationStateMethods } from "./auth.methods.state";
import { NotificationService } from "../../services/notification/notification.service";
import { UserProfileStore } from "../user-profile/user-profile.store";
import { GlobalSpinnerService } from "../../services/global-spinner/global-spinner.service";
import { Router } from "@angular/router";
import { clearCookie } from "../../utils/cookie.util";
import { ProviderStore } from "../provider/provider.store";
import { ProviderServiceStore } from "../provider-service/provider-service.store";

export const withAuthenticationApiMethods = signalStoreFeature(
    { state: type<IAuthenticationState>() },
    withAuthenticationStateMethods,
    withMethods((store) => {
        const authService = inject(AuthService);
        const userProfileService = inject(UserProfileService);
        const notificationService = inject(NotificationService);
        const userProfileStore = inject(UserProfileStore);
        const providerStore = inject(ProviderStore);
        const providerServiceStore = inject(ProviderServiceStore);
        const spinner = inject(GlobalSpinnerService);
        const router = inject(Router);

        const login = rxMethod<AuthLogin>(
            pipe(
                tap(() => {
                    patchState(store, { loading: true, error: null });
                    spinner.show('Signing in, please wait...');
                }),
                exhaustMap((authLogin) =>
                    authService.login(authLogin).pipe(
                        tapResponse({
                            next: (response: { authToken: AuthToken }) => {
                                store.storeAuthToken(response.authToken);
                                if (response.authToken.profile) {
                                    userProfileStore.setUserProfile(response.authToken.profile);
                                }
                                spinner.hide();
                            },
                            error: (error: HttpErrorResponse) => {
                                const message = error.error?.message ?? error.message;
                                patchState(store, { loading: false, error: message });
                                spinner.hide();
                                notificationService.notifyError('Error', message);
                            },
                        })
                    )
                )
            )
        );

        const logout = () => {
            store.clearAuth();
            userProfileStore.clearUserProfile();
            providerStore.clearProvider();
            providerServiceStore.clearProviderService();
            clearCookie('token');
            clearCookie('refreshToken');
            localStorage.clear();
            router.navigate(['/login']);
            authService.logout();
        };

        const register = rxMethod<AuthRegister>(
            pipe(
                tap(() => {
                    patchState(store, { loading: true, error: null });
                    spinner.show('Creating your account, please wait...');
                }),
                exhaustMap((authRegister) =>
                    authService.register(authRegister).pipe(
                        tapResponse({
                            next: (response: { authToken: AuthToken }) => {
                                store.storeAuthToken(response.authToken);
                                if (response.authToken.profile) {
                                    userProfileStore.setUserProfile(response.authToken.profile);
                                }
                                spinner.hide();
                            },
                            error: (error: HttpErrorResponse) => {
                                const message = error.error?.message ?? error.message;
                                patchState(store, { loading: false, error: message });
                                spinner.hide();
                                notificationService.notifyError('Error', message);
                            },
                        })
                    )
                )
            )
        );

        const refreshSession = (): Promise<void> => {
            userProfileStore.rehydrateFromStorage();
            const hadCachedSession = store.rehydrateAuthFromStorage();

            return new Promise((resolve) => {
                authService.refreshToken().subscribe({
                    next: (response: { authToken: AuthToken }) => {
                        store.storeAuthToken(response.authToken);
                        if (response.authToken.profile) {
                            userProfileStore.setUserProfile(response.authToken.profile);
                        }
                        resolve();
                    },
                    error: () => {
                        if (!hadCachedSession && !store.isAuthenticated()) {
                            patchState(store, { loading: false });
                        }
                        resolve();
                    },
                });
            });
        };

        return { login, logout, register, refreshSession };
    })
);